import { SYNC_STATUS } from "../utils/syncUtils";
import { SYNC_STATE_DIRTY } from "../constants";
import { evvRepository } from "../../db/evv";

/**
 * Utility class to handle different save strategies for LookupService
 */
class LookupSaveStrategies {
    constructor(repository, tableName) {
        this.repository = repository;
        this.tableName = tableName;
    }

    /**
     * Clear all local staff data and replace with server data during sync.
     * @param {Array} recordsFromServer - Staff records from server
     * @returns {Promise} Promise that resolves when save is complete
     */
    clearAndSaveStaff = (recordsFromServer) => {
        return this.repository.clearAndSave(recordsFromServer);
    }

    /**
     * Preserve appointments with DeadlockException status before clearing and saving.
     * Merges server appointments with local appointments that have DeadlockException status.
     * Also preserves orphaned appointments (local DeadLock appointments not present in server data).
     * 
     * @param {Array} recordsFromServer - Appointment records from server
     * @param {Object} config - Configuration object with optional properties
     * @param {boolean} config.doNotClear - If true, saves without clearing existing data
     * @param {Object} config.parameters - If present, saves without clearing existing data
     * @returns {Promise} Promise that resolves when save is complete
     */
    preserveAppointments = async (recordsFromServer, config) => {
        try {
            const appointmentsWithDeadLock = await evvRepository.evvDb.appointment
                .where('statusSync')
                .anyOf([SYNC_STATUS.DEADLOCK_EXCEPTION, SYNC_STATUS.ERROR])
                .and(apt => apt.syncState === SYNC_STATE_DIRTY)
                .toArray();

            if (appointmentsWithDeadLock.length === 0) {
                const appointmentsWithDefaultStatus = recordsFromServer.map(serverApt => ({
                    ...serverApt,
                    statusSync: serverApt.statusSync || SYNC_STATUS.SUCCESS,
                }));

                if (config.parameters || config.doNotClear) {
                    return this.repository.saveAll(appointmentsWithDefaultStatus);
                }

                return this.repository.clearAndSave(appointmentsWithDefaultStatus);
            }

            const localDeadLockMap = new Map(
                appointmentsWithDeadLock.map(apt => [apt.appointmentId, apt])
            );

            const processedAppointments = recordsFromServer.map(serverApt => {
                const localAptWithDeadLock = localDeadLockMap.get(serverApt.appointmentId);

                if (localAptWithDeadLock) {
                    return {
                        ...localAptWithDeadLock,
                        appointmentId: serverApt.appointmentId
                    };
                }

                return serverApt;
            });

            const serverAppointmentIds = new Set(recordsFromServer.map(apt => apt.appointmentId));
            const orphanedDeadLockAppointments = [];

            localDeadLockMap.forEach((localApt, appointmentId) => {
                if (!serverAppointmentIds.has(appointmentId)) {
                    orphanedDeadLockAppointments.push(localApt);
                }
            });

            const allAppointmentsToSave = [...processedAppointments, ...orphanedDeadLockAppointments];
            return this.repository.clearAndSave(allAppointmentsToSave);
        } catch (error) {
            return this.repository.clearAndSave(recordsFromServer);
        }
    }

    /**
     * Preserve visits with dirty syncState before clearing and saving
     *
     * This method preserves ALL visits with syncState='dirty' regardless of
     * their appointment's status. This is critical for handling intermittent
     * connectivity scenarios where:
     * - Appointment successfully syncs (statusSync='Success')
     * - But visit fails to sync (syncState='dirty')
     *
     * Without preserving these visits, GPS coordinates and timestamps would be
     * permanently lost when server visits (without GPS) overwrite local data.
     *
     * @param {Array} recordsFromServer - Visit records from server
     * @returns {Promise} Promise that resolves when save is complete
     */
    preserveVisitsAndClearAndSave = async (recordsFromServer) => {
        try {
            const dirtyVisits = await this.repository.loadAllByProperty('syncState', SYNC_STATE_DIRTY);
            const visitsToPreserve = [];

            for (const visit of dirtyVisits) {
                if (visit.appointmentId) {
                    try {
                        // FIX: Preserve ALL dirty visits regardless of appointment status
                        // This handles intermittent connectivity where appointment succeeds
                        // but visit fails (appointment.statusSync='Success', visit.syncState='dirty')
                        //
                        // The visit will be retried in the next sync cycle and cleaned up
                        // after successful synchronization (syncState will change to 'clean')
                        console.log('🔍 [preserveVisitsAndClearAndSave] PRESERVING visit:', {
                            evvVisitId: visit.evvVisitId,
                            appointmentId: visit.appointmentId,
                            syncState: visit.syncState,
                            hasGPS: {
                                lat: visit.startLatitude,
                                lng: visit.startLongitude
                            }
                        });
                        visitsToPreserve.push(visit);
                    } catch (error) {
                        visitsToPreserve.push(visit);
                    }
                }
            }

            const visitsFromServerWithDefaults = recordsFromServer.map(serverVisit => ({
                ...serverVisit,
                statusSync: serverVisit.statusSync || SYNC_STATUS.SUCCESS,
            }));

            const allVisitsToSave = [...visitsFromServerWithDefaults, ...visitsToPreserve];
            return this.repository.clearAndSave(allVisitsToSave);
        } catch (error) {
            return this.repository.clearAndSave(recordsFromServer);
        }
    }

    /**
     * Preserve visits waiting for appointments with DeadLock status
     * @returns {Promise} Promise that resolves when save is complete
     */
    preserveVisitsWaitingForAppointments = async () => {
        if (this.tableName !== 'visit') {
            return this.repository.clearAndSave();
        }

        try {
            const dirtyVisits = await this.repository.loadAllByProperty('syncState', SYNC_STATE_DIRTY);
            const visitsToPreserve = [];

            for (const visit of dirtyVisits) {
                if (visit.appointmentId) {
                    try {
                        // FIX: Apply same fix as preserveVisitsAndClearAndSave
                        // Preserve ALL dirty visits regardless of appointment status
                        const visitWithDefaults = {
                            ...visit,
                            syncState: visit.syncState || SYNC_STATE_DIRTY
                        };

                        console.log('🔍 [preserveVisitsWaitingForAppointments] PRESERVING visit:', {
                            evvVisitId: visit.evvVisitId,
                            appointmentId: visit.appointmentId,
                            syncState: visit.syncState
                        });

                        visitsToPreserve.push(visitWithDefaults);
                    } catch (error) {
                        visitsToPreserve.push(visit);
                    }
                }
            }

            if (visitsToPreserve.length > 0) {
                return this.repository.clearAndSave(visitsToPreserve);
            } else {
                return this.repository.clearAndSave([]);
            }
        } catch (error) {
            return Promise.resolve([]);
        }
    }

    /**
     * Save local appointments or visits with default sync status
     * @param {Array} recordsToSave - Records to save
     * @returns {Promise} Promise that resolves when save is complete
     */
    saveLocalAppointmentsOrVisits = (recordsToSave) => {
        const recordsWithDefaults = recordsToSave.map(record => ({
            ...record,
            statusSync: record.statusSync || SYNC_STATUS.SUCCESS,
            syncState: record.syncState || SYNC_STATE_DIRTY
        }));

        return this.repository.saveAll(recordsWithDefaults);
    }
}

export default LookupSaveStrategies;